import { coerceBhConnectionCount } from '@act/shared/data-transfer-objects';

/** One screening group in the Gap Closures stacked bar chart. */
export interface GapClosuresChartRow {
  group: string;
  inProgress: number;
  scheduled: number;
  completed: number;
  refused: number;
}

/** One HARP status bar. */
export interface HarpEnrollmentChartRow {
  label: string;
  value: number;
}

/** One partner group in the Home Visits stacked bar chart. */
export interface HomeVisitsChartRow {
  group: string;
  inProgram: number;
  completed: number;
  refused: number;
}

const readNumber = (value: unknown): number => coerceBhConnectionCount(value);

/**
 * Maps a gap-closures snapshot to chart rows.
 * Missing or empty `matrix` returns no rows (never hardcoded customer counts).
 * @param data - Section snapshot JSON.
 * @returns Chart rows, or an empty array when warehouse/mock matrix is absent.
 */
export const readGapClosuresChartData = (
  data: Record<string, unknown>,
): GapClosuresChartRow[] => {
  if (!Array.isArray(data.matrix)) {
    return [];
  }

  return data.matrix.flatMap(row => {
    if (!row || typeof row !== 'object') {
      return [];
    }
    const record = row as { screening?: unknown; values?: unknown };
    if (typeof record.screening !== 'string' || !Array.isArray(record.values)) {
      return [];
    }
    return [
      {
        group: record.screening,
        inProgress: readNumber(record.values[0]),
        scheduled: readNumber(record.values[1]),
        completed: readNumber(record.values[2]),
        refused: readNumber(record.values[3]),
      },
    ];
  });
};

/**
 * Maps a harp-enrollment snapshot to chart rows.
 * Missing or empty `statuses` returns no rows (never hardcoded customer counts).
 * @param data - Section snapshot JSON.
 * @returns Chart rows, or an empty array when warehouse/mock statuses are absent.
 */
export const readHarpEnrollmentChartData = (
  data: Record<string, unknown>,
): HarpEnrollmentChartRow[] => {
  if (!Array.isArray(data.statuses)) {
    return [];
  }

  return data.statuses.flatMap(row => {
    if (!row || typeof row !== 'object') {
      return [];
    }
    const record = row as { label?: unknown; value?: unknown };
    if (typeof record.label !== 'string') {
      return [];
    }
    return [{ label: record.label, value: readNumber(record.value) }];
  });
};

/**
 * Maps a home-visits snapshot to chart rows, including a Total group when present.
 * Missing or empty `rows` returns no rows (never hardcoded partner counts).
 * @param data - Section snapshot JSON.
 * @returns Chart rows, or an empty array when warehouse/mock partner rows are absent.
 */
export const readHomeVisitsChartData = (
  data: Record<string, unknown>,
): HomeVisitsChartRow[] => {
  if (!Array.isArray(data.rows)) {
    return [];
  }

  const partnerRows = data.rows.flatMap(row => {
    if (!row || typeof row !== 'object') {
      return [];
    }
    const record = row as { partner?: unknown; values?: unknown };
    if (typeof record.partner !== 'string' || !Array.isArray(record.values)) {
      return [];
    }
    return [
      {
        group: record.partner,
        inProgram: readNumber(record.values[0]),
        completed: readNumber(record.values[1]),
        refused: readNumber(record.values[2]),
      },
    ];
  });

  if (
    partnerRows.length > 0 &&
    Array.isArray(data.totals) &&
    data.totals.length >= 3
  ) {
    partnerRows.push({
      group: 'Total',
      inProgram: readNumber(data.totals[0]),
      completed: readNumber(data.totals[1]),
      refused: readNumber(data.totals[2]),
    });
  }

  return partnerRows;
};
